import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { useProfileHistory } from '../../lib/measurements/use-profiles.js';
import { formatDateTime } from '../../lib/format.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ListSkeleton,
} from '@tailoring-crm/ui';
import { SketchPicture } from './sketch-picture.js';

/**
 * "What did we have before?"
 *
 * Bodies change, and a customer who says "you took these last year and the blouse is tight"
 * is asking a question of record, not of memory. Every edit put the previous values in the
 * audit log; this reads them back as a field-by-field diff, oldest change at the bottom.
 */
export function ProfileHistoryDialog({
  profileId,
  profileName,
  onClose,
}: {
  profileId: string;
  profileName: string;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const history = useProfileHistory(profileId);
  const revisions = history.data ?? [];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('profiles.historyTitle')}</DialogTitle>
          <DialogDescription>{profileName}</DialogDescription>
        </DialogHeader>

        {history.isPending ? (
          <ListSkeleton rows={3} />
        ) : revisions.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('profiles.historyEmpty')}</p>
        ) : (
          <ol className="flex flex-col gap-4">
            {revisions.map((revision, index) => (
              <li key={`${revision.occurredAt}-${index}`} className="flex flex-col gap-1.5">
                <p className="text-sm font-medium">
                  {revision.actorName
                    ? t('profiles.historyBy', {
                        name: revision.actorName,
                        date: formatDateTime(revision.occurredAt),
                      })
                    : t('profiles.historySystem', {
                        date: formatDateTime(revision.occurredAt),
                      })}
                </p>
                {revision.sketch ? (
                  // Both drawings, not just "it changed": the old one is the answer to "what did
                  // we have before?" when the question is about shape rather than inches.
                  <div className="flex flex-col gap-1.5 rounded-md border border-border p-3 text-sm">
                    <span className="font-medium">
                      {revision.sketch.before === null
                        ? t('measurementSketch.historyAdded')
                        : revision.sketch.after === null
                          ? t('measurementSketch.historyRemoved')
                          : t('measurementSketch.historyRedrawn')}
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      {revision.sketch.before ? (
                        <SketchPicture
                          previewMediaId={revision.sketch.before.previewMediaId}
                          name={profileName}
                        />
                      ) : null}
                      {revision.sketch.before && revision.sketch.after ? (
                        <ArrowRight aria-hidden="true" className="size-4 text-muted-foreground" />
                      ) : null}
                      {revision.sketch.after ? (
                        <SketchPicture
                          previewMediaId={revision.sketch.after.previewMediaId}
                          name={profileName}
                        />
                      ) : null}
                    </div>
                  </div>
                ) : null}
                {revision.changes.length === 0 ? (
                  revision.sketch ? null : (
                    <p className="text-sm text-muted-foreground">
                      {t('profiles.historyFirstCapture')}
                    </p>
                  )
                ) : (
                  <ul className="flex flex-col gap-1 rounded-md border border-border p-3 text-sm">
                    {revision.changes.map((change) => (
                      <li key={change.fieldKey} className="flex flex-wrap gap-x-2">
                        <span className="font-medium">{change.label}</span>
                        <span className="text-muted-foreground">
                          {change.before === null
                            ? t('profiles.historyAdded', { after: change.after ?? '' })
                            : change.after === null
                              ? t('profiles.historyRemoved', { before: change.before })
                              : `${t('profiles.historyFrom', { before: change.before })} · ${t(
                                  'profiles.historyTo',
                                  { after: change.after },
                                )}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
